import { OverlayPanel } from "primereact/overlaypanel";
import styled from "styled-components";

/* Hover surfaces are derived from the existing tokens rather than adding new
   flat colors, so they stay in sync if the palette changes. */
const surfaceHover =
  "color-mix(in srgb, var(--brand-purple-700) 10%, transparent)";
const dangerSurfaceHover =
  "color-mix(in srgb, var(--ping-text-color) 8%, transparent)";
/* Kept identical to AccountSwitcher's panel — both are header popovers. */
const panelShadow = [
  "0 4px 12px color-mix(in srgb, var(--brand-black) 6%, transparent)",
  "0 16px 40px color-mix(in srgb, var(--brand-black) 14%, transparent)",
].join(", ");

export const TriggerStyled = styled.button`
  gap: 10px;
  padding: 4px 8px 4px 4px;

  display: flex;
  align-items: center;

  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  font-family: inherit;

  transition: background-color 0.15s ease;

  &:hover,
  &[aria-expanded="true"] {
    background-color: ${surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }
`;

export const AvatarStyled = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background-color: var(--brand-black);

  color: var(--color-white);
  font-size: 13px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.5px;
  line-height: 1;
  user-select: none;
`;

/* Positions the certified check on the avatar's corner. */
export const AvatarWrapStyled = styled.span`
  position: relative;
  display: flex;
  flex-shrink: 0;
`;

/* CLE-3015 — the Clear Certified check on the avatar, only below 1024px, where the name, the role and the
   chip under it are hidden. */
export const AvatarCertifiedBadge = styled.span`
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 16px;
  height: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background-color: var(--color-white);
  color: var(--brand-purple-700);

  .pi {
    font-size: 14px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

/* The chip's line under the role. */
export const CertifiedLineStyled = styled.span`
  margin-top: 3px;
  display: flex;
`;

export const TextContainer = styled.span`
  display: none;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

export const UserStyled = styled.span`
  font-size: 15px;
  font-weight: var(--font-weight-semibold);

  color: var(--brand-black);

  white-space: nowrap;
`;

export const RoleStyled = styled.span`
  font-size: 13px;

  color: var(--color-black-4);

  white-space: nowrap;
`;

export const ChevronStyled = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;

  color: var(--color-black-4);

  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  transition: transform 0.2s ease;
`;

/* PrimeReact draws a caret on the panel via ::before/::after and pads the
   content wrapper — both are reset here so the panel is a plain rounded card. */
export const PanelStyled = styled(OverlayPanel)`
  &.p-overlaypanel {
    width: 280px;
    margin-top: 8px;

    border: 1px solid var(--brand-gray-200);
    border-radius: 16px;
    box-shadow: ${panelShadow};

    &::before,
    &::after {
      display: none;
    }

    .p-overlaypanel-content {
      padding: 16px 10px;
    }
  }
`;

export const DividerStyled = styled.hr`
  height: 1px;
  margin: 6px 0;

  border: none;
  background-color: var(--brand-gray-200);
`;

export const MenuItemStyled = styled.button<{ $danger?: boolean }>`
  gap: 12px;
  padding: 10px 8px;
  width: 100%;

  display: flex;
  align-items: center;

  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  font-family: inherit;
  font-size: 15px;
  text-align: left;

  color: ${({ $danger }) =>
    $danger ? "var(--ping-text-color)" : "var(--brand-black)"};

  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    flex-shrink: 0;
    color: ${({ $danger }) =>
      $danger ? "var(--ping-text-color)" : "var(--brand-gray-600)"};
    transition: color 0.15s ease;
  }

  &:hover {
    background-color: ${({ $danger }) =>
      $danger ? dangerSurfaceHover : surfaceHover};

    color: ${({ $danger }) =>
      $danger ? "var(--ping-text-color)" : "var(--brand-purple-700)"};

    svg {
      color: ${({ $danger }) =>
        $danger ? "var(--ping-text-color)" : "var(--brand-purple-700)"};
    }
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: -2px;
  }
`;

/* Shorter than the other rows — height only, so the type and icon still match. */
export const LogoutItemStyled = styled(MenuItemStyled)`
  padding-top: 6px;
  padding-bottom: 6px;
`;
